import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Pulls DoseSpot's prescriptions for one patient back into the chart as
 * `MedicationRequest` resources (§11).
 *
 * **The guarantee is "the chart is correct whenever anyone looks at it."** Two triggers
 * deliver it: the provider closing the Prescribe modal, and any chart view of a patient
 * that carries a DoseSpot identifier. There is no cron, no bot, and the notification
 * webhook is deliberately not a write-back dependency (§12.7).
 *
 * **No FHIR at the boundary** (`AGENTS.md`): this returns counts, not medications. The
 * chart re-reads FHIR through its own existing queries once the sync lands.
 */
export const doseSpotSyncPrescriptions = oc
  .route({ method: 'POST', path: '/v2/dosespot/sync-prescriptions' })
  .input(
    z.object({
      patientId: z.string().min(1),
      /**
       * The **chart-view** trigger sets this; the modal-close trigger does not (§11.1).
       *
       * A debounced call is skipped when another sync ran for this patient inside the
       * window. It is a debounce, not a lock: concurrent syncs converge because every write
       * is a conditional upsert, and a skipped sync is harmless because the next view syncs.
       *
       * Modal close is an explicit user action that just produced new prescriptions, so it
       * always runs — hence the default is off rather than on.
       */
      debounce: z.boolean().optional(),
    }),
  )
  .output(
    z.object({
      /** Prescriptions upserted onto the chart. */
      synced: z.number(),
      /**
       * Rows that failed and were skipped. Each upserts independently — one bad row logs
       * and skips, the rest of the batch still lands, and the next run converges (§11.3).
       *
       * Zero on both counts is also how "nothing to do" reports: the feature is
       * unconfigured, the patient was never launched into DoseSpot, or the debounce
       * suppressed this call.
       */
      skipped: z.number(),
    }),
  )
